import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Modal, Select } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { getAllCompanies } from "../../../core/data/redux/services/companyService";
import {
  createWebhookIntegration,
  deleteWebhookIntegration,
  fetchWebhookIntegrations,
  regenerateWebhookKey,
  toggleWebhookIntegrationStatus,
  updateWebhookIntegration,
} from "../../../core/data/redux/services/webhookIntegrationApi";
import { getErrorMessage } from "../../../core/data/redux/utils/errorUtils";
import { notify } from "../../../services/toast.config";
import { all_routes } from "../../router/all_routes";
import { ConnectWebhookModal } from "./components/modals/ConnectWebhookModal";
import { WebhookUrlModal } from "./components/modals/WebhookUrlModal";
import { WebhookEventsDrawer } from "./components/WebhookEventsDrawer";
import { WebhookIntegrationsTable } from "./components/WebhookIntegrationsTable";
import { hasModulePermission } from "./helpers/permissions";

import type {
  CompanyOption,
  WebhookIntegration,
  WebhookIntegrationForm,
  WebhookIntegrationWithSecret,
  WebhookSecretSetup,
} from "./types";

import { INTEGRATION_STATUS, WEBHOOK_MODULE } from "./constants";

const WebhookIntegrationsPage: React.FC = () => {
  const user = useSelector((state: any) => state.user.user);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [integrations, setIntegrations] = useState<WebhookIntegration[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState<WebhookIntegration | null>(null);
  const [urlIntegration, setUrlIntegration] = useState<WebhookIntegration | null>(null);
  const [secretSetup, setSecretSetup] = useState<WebhookSecretSetup | null>(null);
  const [eventsIntegration, setEventsIntegration] = useState<WebhookIntegration | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const routes = all_routes;
  const canEdit = hasModulePermission(user?.userDetails, WEBHOOK_MODULE, "Edit");
  const canDelete = hasModulePermission(user?.userDetails, WEBHOOK_MODULE, "Delete");

  const selectedCompany = useMemo(
    () => companies.find((company) => company.id === selectedCompanyId) || null,
    [companies, selectedCompanyId],
  );

  const scope = useMemo(
    () =>
      selectedCompany
        ? { companyId: selectedCompany.id, companyType: selectedCompany.type }
        : null,
    [selectedCompany],
  );

  const companyOptions = useMemo(
    () =>
      companies.map((company) => ({
        value: company.id,
        label: `${company.name} - ${company.type}`,
      })),
    [companies],
  );

  const loadIntegrations = useCallback(async () => {
    if (!scope) return;
    setIsLoading(true);
    try {
      setIntegrations(await fetchWebhookIntegrations(scope));
    } catch (error) {
      notify.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    getAllCompanies(localStorage.getItem("signature"))
      .then((list: CompanyOption[]) => {
        setCompanies(list);
        if (list.length) setSelectedCompanyId(list[0].id);
      })
      .catch((error: unknown) => notify.error(getErrorMessage(error)));
  }, []);

  useEffect(() => {
    loadIntegrations();
  }, [loadIntegrations]);

  const replaceIntegration = (updated: WebhookIntegration) => {
    setIntegrations((prev) =>
      prev.map((item) =>
        item.id === updated.id
          ? {
              ...item,
              ...updated,
              lead_count: item.lead_count,
              conversation_count: item.conversation_count,
            }
          : item,
      ),
    );
  };

  const showOneTimeSecret = (
    { webhook_secret, webhook_secret_header, ...integration }: WebhookIntegrationWithSecret,
    isRegenerated: boolean,
  ): WebhookIntegration => {
    setSecretSetup({
      headerName: webhook_secret_header,
      headerValue: webhook_secret,
      isRegenerated,
    });
    setUrlIntegration(integration);
    return integration;
  };

  const handleOpenCreate = () => {
    setEditingIntegration(null);
    setShowConnectModal(true);
  };

  const handleOpenEdit = (integration: WebhookIntegration) => {
    setEditingIntegration(integration);
    setShowConnectModal(true);
  };

  const handleCloseConnect = () => {
    setShowConnectModal(false);
    setEditingIntegration(null);
  };

  const handleSubmit = async (form: WebhookIntegrationForm) => {
    if (!scope) return;
    setIsSubmitting(true);
    try {
      if (editingIntegration) {
        replaceIntegration(
          await updateWebhookIntegration(scope, editingIntegration.id, {
            name: form.name,
            business_phone: form.business_phone,
            waba_id: form.waba_id,
            auto_create_lead: form.auto_create_lead,
          }),
        );
        notify.success("Webhook connection updated");
      } else {
        const created = showOneTimeSecret(await createWebhookIntegration(scope, form), false);
        setIntegrations((prev) => [created, ...prev]);
        notify.success("Webhook connection created");
      }
      handleCloseConnect();
    } catch (error) {
      notify.error(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async (integration: WebhookIntegration, isActive: boolean) => {
    if (!scope) return;
    setTogglingId(integration.id);
    try {
      replaceIntegration(
        await toggleWebhookIntegrationStatus(
          scope,
          integration.id,
          isActive ? INTEGRATION_STATUS.ACTIVE : INTEGRATION_STATUS.DISABLED,
        ),
      );
      notify.success(isActive ? "Webhook enabled" : "Webhook disabled");
    } catch (error) {
      notify.error(getErrorMessage(error));
    } finally {
      setTogglingId(null);
    }
  };

  const handleRegenerate = (integration: WebhookIntegration) => {
    if (!scope) return;
    Modal.confirm({
      title: `Regenerate URL & secret for "${integration.name}"?`,
      content:
        "The current webhook URL and secret stop working immediately. Events are rejected until you update both in your Fonada dashboard.",
      okText: "Regenerate",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          replaceIntegration(
            showOneTimeSecret(await regenerateWebhookKey(scope, integration.id), true),
          );
          notify.success("Webhook URL and secret regenerated");
        } catch (error) {
          notify.error(getErrorMessage(error));
        }
      },
    });
  };

  const handleDelete = (integration: WebhookIntegration) => {
    if (!scope) return;
    Modal.confirm({
      title: `Delete "${integration.name}"?`,
      content:
        "The webhook URL stops working. Leads and conversations already received stay in the CRM.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteWebhookIntegration(scope, integration.id);
          setIntegrations((prev) => prev.filter((item) => item.id !== integration.id));
          notify.success("Webhook connection deleted");
        } catch (error) {
          notify.error(getErrorMessage(error));
        }
      },
    });
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">WhatsApp Connect</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">CRM</li>
                <li className="breadcrumb-item active" aria-current="page">
                  Webhook Integration
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
            <Select
              style={{ minWidth: 240 }}
              className="mb-2"
              placeholder="Select company"
              options={companyOptions}
              value={selectedCompanyId ?? undefined}
              onChange={setSelectedCompanyId}
              showSearch
              optionFilterProp="label"
            />
            {canEdit && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                className="mb-2 d-flex align-items-center"
                onClick={handleOpenCreate}
                disabled={!scope}>
                Connect New
              </Button>
            )}
            <div className="head-icons mb-2">
              <CollapseHeader />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h5 className="mb-1">Connections</h5>
            <small className="text-muted">
              Leads and chats from these webhooks land in the Lead Bank, with the full WhatsApp
              conversation attached to each lead.
            </small>
          </div>
          <div className="card-body">
            <WebhookIntegrationsTable
              integrations={integrations}
              isLoading={isLoading}
              canEdit={canEdit}
              canDelete={canDelete}
              togglingId={togglingId}
              onViewUrl={setUrlIntegration}
              onViewEvents={setEventsIntegration}
              onEdit={handleOpenEdit}
              onToggleStatus={handleToggleStatus}
              onRegenerate={handleRegenerate}
              onDelete={handleDelete}
            />
          </div>
        </div>
      </div>

      <ConnectWebhookModal
        open={showConnectModal}
        integration={editingIntegration}
        isSubmitting={isSubmitting}
        onCancel={handleCloseConnect}
        onSubmit={handleSubmit}
      />
      <WebhookUrlModal
        integration={urlIntegration}
        secretSetup={secretSetup}
        onClose={() => {
          setSecretSetup(null);
          setUrlIntegration(null);
        }}
      />
      {scope && (
        <WebhookEventsDrawer
          integration={eventsIntegration}
          companyId={scope.companyId}
          companyType={scope.companyType}
          onClose={() => setEventsIntegration(null)}
        />
      )}
    </div>
  );
};

export default WebhookIntegrationsPage;
